import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import Header from "components/headers/light";
import Footer from "components/footers/MiniCenteredFooter";
import { enrollmentApi } from "services/enrollmentApi";
import EnrollModal from "components/enrollment/EnrollModal";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "services/authApi";
import {
  getBlockingEnrollmentTracks,
  findTrackForCourse,
  getTrackedEmail,
  saveEnrollmentTrack,
} from "utils/enrollmentStorage";
import { Toast } from "helpers/Alert";

const courseIdOf = (course) => course?._id || course?.id || null;

const Page = styled.div`
  min-height: 80vh;
  background: #fff;
`;

const Hero = styled(motion.div)`
  text-align: center;
  padding: 60px 20px 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(28px, 4vw, 48px);
  font-weight: 900;
  color: #111827;
  line-height: 1.15;
  margin: 0 0 16px;
  span {
    color: #6415ff;
  }
`;

const HeroText = styled.p`
  color: #6b7280;
  font-size: 16px;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 50px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
`;

const Card = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  transition: box-shadow 0.25s, transform 0.25s;
  &:hover {
    box-shadow: 0 12px 40px rgba(100, 21, 255, 0.12);
    transform: translateY(-4px);
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6415ff;
  background: rgba(100, 21, 255, 0.1);
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 14px;
  align-self: flex-start;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 10px;
`;

const CardDesc = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.7;
  flex: 1;
  margin: 0 0 16px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 20px;
`;

const EnrollBtn = styled.button`
  display: block;
  width: 100%;
  margin-top: auto;
  flex-shrink: 0;
  padding: 13px 16px;
  border: ${(p) => (p.$outline ? "2px solid #6415ff" : "none")};
  border-radius: 10px;
  background-color: ${(p) => (p.$outline ? "#fff" : "#6415ff")};
  background-image: ${(p) =>
    p.$outline ? "none" : "linear-gradient(90deg, #6415ff, #430ce5)"};
  color: ${(p) => (p.$outline ? "#6415ff" : "#ffffff")};
  font-weight: 700;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.25;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(p) =>
      p.$outline ? "0 4px 12px rgba(100, 21, 255, 0.15)" : "0 8px 20px rgba(100, 21, 255, 0.35)"};
    color: ${(p) => (p.$outline ? "#430ce5" : "#ffffff")};
  }
`;

const StatusNote = styled.p`
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) =>
    p.$status === "pending" ? "#92400e" : p.$status === "completed" || p.$status === "approved" ? "#065f46" : "#6b7280"};
`;

const DashLink = styled.button`
  margin-top: 20px;
  padding: 12px 28px;
  border: 2px solid #6415ff;
  border-radius: 10px;
  background: transparent;
  color: #6415ff;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #6415ff;
    color: #fff;
  }
`;

const TrackBanner = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto 24px;
  padding: 16px 20px;
  border-radius: 14px;
  background: linear-gradient(90deg, #faf5ff, #eff6ff);
  border: 1px solid #e8d5ff;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #374151;

  strong {
    color: #6415ff;
  }
`;

const TrackBtn = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(90deg, #6415ff, #430ce5);
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
`;

const EmptyNote = styled.p`
  color: #9ca3af;
  font-size: 15px;
  margin: 0;
`;

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const formatSessionDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Date TBA";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

export default function Schedule() {
  const [courses, setCourses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSessionTitle, setSelectedSessionTitle] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [myEnrollments, setMyEnrollments] = useState(() => getBlockingEnrollmentTracks());
  const navigate = useNavigate();
  const auth = getAuthToken();
  const pendingTrack = myEnrollments.find((t) => t.status === "pending");
  const approvedTrack = myEnrollments.find((t) =>
    ["approved", "completed"].includes(t.status)
  );

  const applyEnrollmentList = (list) => {
    (list || []).forEach((e) =>
      saveEnrollmentTrack({
        id: e.id || e._id,
        email: e.email,
        fullName: e.fullName,
        courseTitle: e.courseTitle || e.course?.title,
        courseId: e.courseId || e.course?._id || e.course?.id,
        status: e.status,
      })
    );
    setMyEnrollments(getBlockingEnrollmentTracks());
  };

  useEffect(() => {
    Promise.all([enrollmentApi.getCourses(), enrollmentApi.getSchedules()])
      .then(([coursesRes, schedulesRes]) => {
        setCourses(coursesRes.data.courses || []);
        setSchedules(schedulesRes.data.schedules || []);
      })
      .catch(() => {
        setCourses([]);
        setSchedules([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncEnrollments = async () => {
      try {
        if (auth?.token && auth.role === "student") {
          const { data } = await enrollmentApi.getDashboard();
          if (cancelled) return;
          const apps = data.applications || [];
          apps.forEach((e) =>
            saveEnrollmentTrack({
              id: e.id || e._id,
              email: data.user?.email || auth.email,
              fullName: e.fullName || data.user?.name,
              courseTitle: e.courseTitle || e.course?.title,
              courseId: e.courseId || e.course?._id || e.course?.id,
              status: e.status,
            })
          );
          (data.courses || []).forEach((c) => {
            const id = c._id || c.id;
            const existing = findTrackForCourse(c, getBlockingEnrollmentTracks());
            if (!existing) {
              saveEnrollmentTrack({
                id: `course-${id}`,
                email: data.user?.email || auth.email,
                courseTitle: c.title,
                courseId: id,
                status: "completed",
              });
            }
          });
          setMyEnrollments(getBlockingEnrollmentTracks());
          return;
        }

        const email = getTrackedEmail() || auth?.email;
        if (!email) {
          setMyEnrollments(getBlockingEnrollmentTracks());
          return;
        }
        const { data } = await enrollmentApi.getEnrollmentsByEmail(email);
        if (cancelled) return;
        applyEnrollmentList(data.enrollments);
      } catch {
        if (!cancelled) setMyEnrollments(getBlockingEnrollmentTracks());
      }
    };

    syncEnrollments();
    const timer = setInterval(syncEnrollments, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [auth?.token, auth?.role, auth?.email]);

  const statusForCourse = (course) => findTrackForCourse(course, myEnrollments);

  const openEnroll = (course, sessionTitle = "") => {
    const courseId = courseIdOf(course);
    if (!courseId) {
      Toast({
        message: "This session has no linked course. Ask admin to fix the schedule.",
        type: "error",
      });
      return;
    }

    const already = statusForCourse(course);
    if (already) {
      if (already.status === "pending") {
        Toast({ message: "You already applied for this course. Status is Pending.", type: "info" });
        // navigate("/my-enrollment");
        return;
      }
      Toast({ message: "You are already enrolled in this course.", type: "info" });
      navigate(auth ? "/dashboard" : "/login");
      return;
    }

    setSelectedCourse({ ...course, _id: courseId, id: courseId });
    setSelectedSessionTitle(sessionTitle);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCourse(null);
    setSelectedSessionTitle("");
  };

  const renderCourseAction = (course, sessionTitle = "") => {
    const track = statusForCourse(course);
    if (track?.status === "pending") {
      return (
        <>
          <StatusNote $status="pending">Application pending admin approval</StatusNote>
          {/* <EnrollBtn $outline type="button" onClick={() => navigate("/my-enrollment")}>
            Track Enrollment
          </EnrollBtn> */}
        </>
      );
    }
    if (track && ["approved", "completed"].includes(track.status)) {
      return (
        <>
          <StatusNote $status="completed">Already enrolled in this course</StatusNote>
          <EnrollBtn $outline type="button" onClick={() => navigate(auth ? "/dashboard" : "/login")}>
            {auth ? "Go to Dashboard" : "Sign In to Dashboard"}
          </EnrollBtn>
        </>
      );
    }
    return (
      <EnrollBtn
        type="button"
        className="schedule-action-btn"
        onClick={() => openEnroll(course, sessionTitle)}
      >
        Enroll Now
      </EnrollBtn>
    );
  };

  return (
    <>
      <AnimationRevealPage>
        <Header roundedHeaderButton />
        <Page>
          <Hero initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <HeroTitle>
              Explore Our <span>Training Courses</span>
            </HeroTitle>
            <HeroText>
              Browse available courses and upcoming class sessions. Enroll to start your journey with ADCS Tech
              Solutions.
            </HeroText>
            {!auth && (pendingTrack || approvedTrack) && (
              <TrackBanner initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <span>
                  {approvedTrack && approvedTrack.status !== "pending" ? (
                    <>
                      <strong>Already enrolled!</strong> Sign in for {approvedTrack.courseTitle}.
                    </>
                  ) : (
                    <>
                      You have a <strong>pending</strong> enrollment for {pendingTrack.courseTitle}.
                    </>
                  )}
                </span>
                {/* <TrackBtn type="button" onClick={() => navigate("/my-enrollment")}>
                  Track My Enrollment
                </TrackBtn> */}
              </TrackBanner>
            )}
            {auth?.role === "student" && (
              <DashLink onClick={() => navigate("/dashboard")}>Go to My Dashboard</DashLink>
            )}
          </Hero>

          {loading ? (
            <p style={{ textAlign: "center", color: "#9ca3af" }}>Loading schedule...</p>
          ) : (
            <>
              <Section>
                <SectionTitle>Upcoming Class Sessions</SectionTitle>
                {schedules.length === 0 ? (
                  <EmptyNote>No class sessions published yet. Check back soon or enroll in a course below.</EmptyNote>
                ) : (
                  <Grid>
                    {schedules.map((session, i) => (
                      <Card
                        key={session._id || session.id}
                        custom={i}
                        initial="hidden"
                        animate="visible"
                        variants={cardVariants}
                      >
                        <Badge>{session.course?.category || "Class"}</Badge>
                        <CardTitle>{session.title}</CardTitle>
                        <CardDesc>{session.course?.title}</CardDesc>
                        <Meta>
                          <span>{formatSessionDate(session.date)}</span>
                          <span>
                            {session.startTime} to {session.endTime}
                          </span>
                          <span>Instructor: {session.instructor}</span>
                        </Meta>
                        {renderCourseAction(session.course, session.title)}
                      </Card>
                    ))}
                  </Grid>
                )}
              </Section>

              <Section>
                <SectionTitle>All Training Courses</SectionTitle>
                <Grid>
                  {courses.map((course, i) => (
                    <Card
                      key={course._id || course.id}
                      custom={i}
                      initial="hidden"
                      animate="visible"
                      variants={cardVariants}
                    >
                      <Badge>{course.category}</Badge>
                      <CardTitle>{course.title}</CardTitle>
                      <CardDesc>{course.description}</CardDesc>
                      <Meta>
                        <span>{course.duration}</span>
                        <span>•</span>
                        <span>{course.level}</span>
                      </Meta>
                      {renderCourseAction(course)}
                    </Card>
                  ))}
                </Grid>
              </Section>
            </>
          )}
        </Page>
        <Footer />
      </AnimationRevealPage>

      <EnrollModal
        course={selectedCourse}
        sessionTitle={selectedSessionTitle}
        isOpen={modalOpen}
        onClose={closeModal}
      />
    </>
  );
}
